import React from "react";
import { Clock, ClipboardCheck, Sparkles, CheckCircle2, Wrench } from "lucide-react";

// Which descriptive scope tiers are included for a given detail level.
const LEVEL_TIERS = {
  essential: ["essential"],
  deep: ["essential", "deep"],
  ascendia: ["essential", "deep", "ascendia"],
};
const TIER_LABEL = { essential: "Essential", deep: "Deep", ascendia: "Ascendia Detail" };

/**
 * Per-room breakdown of exactly what's included for the chosen detail level.
 * Descriptive only — does not compute price (the estimator engine owns pricing).
 */
export function RoomBreakdown({ areas = [], levelKey = "essential", cardCls, kicker }) {
  const tiers = LEVEL_TIERS[levelKey] || ["essential"];
  if (!areas.length) return null;
  return (
    <div className={cardCls} data-testid="pi-room-breakdown">
      <p className={kicker}>Room-by-room breakdown</p>
      <p className="mt-1 text-sm text-muted-c">Exactly what your <span className="text-gold-soft">{TIER_LABEL[levelKey] || "plan"}</span> level includes in each space.</p>
      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        {areas.map((a) => {
          const Icon = a.Icon;
          const items = tiers.flatMap((t) => (a.scope?.[t] || []).map((label) => ({ label, tier: t })));
          return (
            <div key={a.id} className="rounded-xl border border-[color:var(--border-subtle)] bg-black/30 p-4">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-[color:var(--border-strong)] bg-gold/10">{Icon ? <Icon className="h-4 w-4 text-gold-soft" /> : <Sparkles className="h-4 w-4 text-gold-soft" />}</span>
                <p className="text-sm font-medium text-white">{a.name}</p>
              </div>
              <ul className="mt-3 space-y-1.5">
                {items.map((it, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className={`mt-0.5 h-3.5 w-3.5 flex-shrink-0 ${it.tier === "ascendia" ? "text-gold" : "text-gold-soft/70"}`} />
                    <span className="text-secondary">{it.label}</span>
                  </li>
                ))}
              </ul>
              {a.micro?.length > 0 && levelKey === "ascendia" && (
                <p className="mt-3 text-[11px] text-muted-c"><span className="text-gold-soft">What Ascendia sees:</span> {a.micro.join(" · ")}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Visual project timeline derived from the estimator's duration band.
 * Phases are indicative; final schedule is confirmed by Ascendia.
 */
export function TimelinePhases({ dur, cardCls, kicker }) {
  const band = dur?.band && dur.band !== "—" ? dur.band : (dur?.low ? `${dur.low}–${dur.high} hrs` : "To be confirmed");
  const phases = [
    { Icon: ClipboardCheck, t: "Preparation & access", d: "We confirm scope, arrival window and access. You clear valuables and fragile items.", tag: "Before the day" },
    { Icon: Wrench, t: "On-site detailing", d: "The team works surface-by-surface through every room in your plan.", tag: band },
    { Icon: Sparkles, t: "Final inspection & handover", d: "A detail-by-detail walkthrough — tracks, hinges and crevices — before we hand back.", tag: "Same visit" },
  ];
  return (
    <div className={cardCls} data-testid="pi-timeline">
      <p className={kicker}>Your project timeline</p>
      <div className="mt-4 relative pl-6">
        <div className="absolute left-[9px] top-1 bottom-1 w-px bg-[color:var(--border-strong)]" aria-hidden="true" />
        <div className="space-y-5">
          {phases.map((p, i) => {
            const Icon = p.Icon;
            return (
              <div key={i} className="relative">
                <span className="absolute -left-6 top-0 flex h-[19px] w-[19px] items-center justify-center rounded-full border border-[color:var(--border-strong)] bg-[hsl(var(--background))]">
                  <span className="h-2 w-2 rounded-full bg-[color:var(--gold)]" />
                </span>
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-gold-soft" />
                  <p className="text-sm font-medium text-white">{p.t}</p>
                  <span className="ml-auto inline-flex items-center gap-1 rounded-full border border-[color:var(--border-subtle)] px-2.5 py-0.5 text-[11px] text-gold-soft tabular-nums"><Clock className="h-3 w-3" /> {p.tag}</span>
                </div>
                <p className="mt-1 text-xs text-muted-c">{p.d}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
